import type { CheckoutSession, AttemptResult } from '@/lib/types';
import { CheckoutHeader } from '@/components/CheckoutHeader';

interface Props {
  session: CheckoutSession;
  attempt: AttemptResult;
}

export function QrDisplay({ session, attempt }: Props) {
  const nextAction = attempt.next_action;
  const data = (nextAction.data ?? {}) as Record<string, unknown>;
  const qrImageBase64 = (data.qrImageBase64 ?? nextAction.qrImageBase64) as string | undefined;
  const imageUrl = (data.imageUrl ?? nextAction.imageUrl) as string | undefined;
  const expiresAt = (data.expiresAt ?? nextAction.expiresAt) as string | undefined;

  const amountFormatted = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: session.currency,
    minimumFractionDigits: 0,
  }).format(parseFloat(session.amount));

  const expiresFormatted = expiresAt
    ? new Intl.DateTimeFormat('es-CO', { hour: '2-digit', minute: '2-digit' }).format(new Date(expiresAt))
    : null;

  const API_BASE = import.meta.env.VITE_API_URL ?? window.location.origin;
  const qrSrc = qrImageBase64
    ? (qrImageBase64.startsWith('data:') ? qrImageBase64 : `data:image/png;base64,${qrImageBase64}`)
    : imageUrl
      ? (imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`)
      : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-8 pb-8">
      <div className="w-full max-w-md">
        {/* Merchant + amount header */}
        <CheckoutHeader session={session} />

        {/* QR Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm font-bold">B</span>
            </div>
            <h2 className="text-base font-semibold text-gray-900">Pago BRE-B con QR</h2>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Escanea el código QR con tu app bancaria para completar el pago.
          </p>

          {/* QR Image */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 mb-4 flex flex-col items-center">
            <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Código QR BRE-B</p>
            {qrSrc ? (
              <img
                src={qrSrc}
                alt="Código QR BRE-B"
                className="w-48 h-48 rounded-lg"
              />
            ) : (
              <div className="w-48 h-48 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-400 text-sm">
                QR no disponible
              </div>
            )}
          </div>

          {/* Instructions */}
          <ol className="text-sm text-gray-600 space-y-2 mb-4">
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">1.</span>
              Abre tu app bancaria y selecciona <strong>Pagar con QR</strong>.
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">2.</span>
              Escanea el código de arriba con la cámara.
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">3.</span>
              Confirma el pago de <strong>{amountFormatted}</strong>.
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">4.</span>
              Esta página se actualizará automáticamente al confirmar tu pago.
            </li>
          </ol>

          {/* Expiry */}
          {expiresFormatted && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>El QR expira a las {expiresFormatted}</span>
            </div>
          )}
        </div>

        {/* Polling indicator */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-400 py-2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin" />
          <span>Esperando confirmación de pago...</span>
        </div>

        <p className="text-center text-xs text-gray-400 mt-2">
          Puedes cerrar esta página con seguridad. Volveremos a sincronizar el estado automáticamente al regresar.
        </p>

        <p className="text-center text-xs text-gray-400 mt-2 mb-2">
          Pago procesado de forma segura por Orangepill
        </p>
      </div>
    </div>
  );
}
